// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 7 — "The lists": when honours arrive.
//
// The register's `list` field is unnormalised free text. Two things follow, and
// both are drawn rather than hidden:
//
//   1. The YEAR shown here is the year written into the list's NAME, not the
//      award date. Recent lists are gazetted under bare strings ("Australia
//      Day", "The King's Birthday"), so the dated series collapses after a
//      cutoff that is DERIVED here, not hardcoded, and the collapsed span is
//      shaded rather than drawn as a fall in awards.
//   2. Whole occasions are mostly undated — 91% of bravery records carry no
//      year in the list name. Every card states its dated share on the face of
//      the chart, so nobody reads "bravery began in 2020".

import { systemColour } from '../palette';
import { escapeHtml, num, pct, tipAttr } from '../format';
import { gloss } from '../glossary';
import type { ListEntry, View } from '../types';

interface OccSpec {
  id: string;
  label: string;
  cadence: string;
  blurb: string;
  colour: string;
}

/**
 * Colours identify the LIST and are borrowed wholesale from the site's award
 * system palette — nothing new is invented here (see palette.ts).
 */
const SPECS: OccSpec[] = [
  {
    id: 'Australia Day',
    label: 'Australia Day list',
    cadence: 'Twice a year · announced 26 January',
    blurb: 'The larger of the two general lists.',
    colour: systemColour('Order of Australia'),
  },
  {
    id: "Monarch's Birthday",
    label: "Monarch's Birthday list",
    cadence: 'Twice a year · announced in June',
    blurb: 'The Queen’s Birthday list until 2022, the King’s Birthday list since.',
    colour: systemColour('Realm'),
  },
  {
    id: 'Bravery Decorations',
    label: 'Bravery decorations',
    cadence: 'Continuous · as incidents are assessed',
    blurb: 'Announced in batches through the year, not on the calendar.',
    colour: systemColour('Bravery'),
  },
  {
    id: 'Special List',
    label: 'Special lists',
    cadence: 'Occasional · one-off announcements',
    blurb: 'Stood up for a particular occasion or operation.',
    colour: systemColour('Meritorious'),
  },
  {
    id: 'Other list',
    label: 'Other named lists',
    cadence: 'Occasional · everything else with a name',
    blurb: 'Gallantry batches, Midwinter’s Day, named commemorations.',
    colour: systemColour('Unknown'),
  },
  {
    id: 'Military Honours',
    label: 'Military honours',
    cadence: 'Continuous · as decorations are approved',
    blurb: 'Only a handful of records carry this list name.',
    colour: systemColour('Defence'),
  },
];

interface Occ extends OccSpec {
  byYear: Map<number, number>;
  years: number[];
  dated: number;
  undated: number;
  total: number;
  peakYear: number | null;
  peakN: number;
}

export const listsView: View = (root, ctx) => {
  const core = ctx.core;
  const entries: ListEntry[] = core.lists.entries;

  // ---- shape the data -----------------------------------------------------

  const occs: Occ[] = SPECS.map((spec) => {
    const rows = entries.filter((e) => e.occasion === spec.id);
    const byYear = new Map<number, number>();
    let undated = 0;
    for (const r of rows) {
      if (r.year == null) undated += r.n;
      else byYear.set(r.year, (byYear.get(r.year) ?? 0) + r.n);
    }
    const years = [...byYear.keys()].sort((a, b) => a - b);
    let dated = 0;
    let peakYear: number | null = null;
    let peakN = 0;
    for (const y of years) {
      const n = byYear.get(y) ?? 0;
      dated += n;
      if (n > peakN) {
        peakN = n;
        peakYear = y;
      }
    }
    return { ...spec, byYear, years, dated, undated, total: dated + undated, peakYear, peakN };
  }).sort((a, b) => b.total - a.total);

  const allYears = entries.filter((e) => e.year != null).map((e) => e.year as number);
  const minYear = allYears.length ? Math.min(...allYears) : 1975;
  const maxYear = allYears.length ? Math.max(...allYears) : minYear + 1;
  const span = Math.max(1, maxYear - minYear);

  const listed = occs.reduce((a, o) => a + o.total, 0);
  const records = core.totals.records;
  const noList = Math.max(0, records - listed);

  const ad = occs.find((o) => o.id === 'Australia Day');
  const mb = occs.find((o) => o.id === "Monarch's Birthday");
  const twoLists = (ad?.total ?? 0) + (mb?.total ?? 0);

  /**
   * DERIVED cutoff, not a hardcoded year. Walk the years and keep the last one
   * where the two calendar lists still account for at least half of that year's
   * Order of Australia awards. After it, the lists are still being gazetted —
   * their names have simply stopped carrying a year.
   */
  const oaByYear = new Map<number, number>();
  for (const y of core.byYear) oaByYear.set(y.year, y.counts['Order of Australia'] ?? 0);
  let datedThrough = maxYear;
  for (const [year, oa] of [...oaByYear.entries()].sort((a, b) => a[0] - b[0])) {
    if (year < minYear || year > maxYear || oa <= 0) continue;
    const calendar = (ad?.byYear.get(year) ?? 0) + (mb?.byYear.get(year) ?? 0);
    if (calendar >= 0.5 * oa) datedThrough = year;
  }
  const hasBand = datedThrough < maxYear;

  const sharedMax = Math.max(1, ...occs.map((o) => o.peakN));

  // ---- view state ---------------------------------------------------------

  const params = new URLSearchParams(root.dataset.params ?? '');
  const wanted = params.get('occasion') ?? '';
  let selected = occs.findIndex((o) => o.id === wanted);
  let sharedScale = params.get('scale') !== 'own';

  // ---- geometry -----------------------------------------------------------

  const W = 300;
  const H = 132;
  const PAD_L = 38;
  const PAD_R = 8;
  const PAD_T = 10;
  const PAD_B = 24;
  const PW = W - PAD_L - PAD_R;
  const PH = H - PAD_T - PAD_B;
  const BASE = PAD_T + PH;

  const xOf = (year: number): number => PAD_L + ((year - minYear) / span) * PW;
  const step = PW / span;

  function yOf(n: number, top: number): number {
    const t = top > 0 ? n / top : 0;
    const v = BASE - Math.max(0, Math.min(1, t)) * PH;
    return Number.isFinite(v) ? v : BASE;
  }

  const axisLabel = (n: number): string => num(Math.round(n));

  // ---- one mini time series ----------------------------------------------

  function miniChart(o: Occ, top: number): string {
    const parts: string[] = [];

    parts.push(
      `<rect x="${PAD_L}" y="${PAD_T}" width="${PW.toFixed(1)}" height="${PH}" fill="var(--bg-base)"></rect>`,
    );

    // The span whose list names carry no year, shaded rather than drawn as a fall.
    if (hasBand) {
      const bx = xOf(datedThrough) + step / 2;
      const bw = Math.max(2, xOf(maxYear) + step / 2 - bx);
      parts.push(
        `<rect x="${bx.toFixed(1)}" y="${PAD_T}" width="${bw.toFixed(1)}" height="${PH}" fill="#b3701a" opacity="0.1"></rect>`,
        `<line x1="${bx.toFixed(1)}" y1="${PAD_T}" x2="${bx.toFixed(1)}" y2="${BASE}" stroke="#b3701a" stroke-width="0.8" stroke-dasharray="2 2"></line>`,
      );
    }

    // Gridlines + y axis: zero, midpoint, top.
    for (const frac of [0, 0.5, 1]) {
      const v = top * frac;
      const y = yOf(v, top);
      parts.push(
        `<line x1="${PAD_L}" y1="${y.toFixed(1)}" x2="${(PAD_L + PW).toFixed(1)}" y2="${y.toFixed(1)}" stroke="var(--border-subtle)" stroke-width="0.7"></line>`,
        `<text x="${PAD_L - 4}" y="${(y + 3).toFixed(1)}" text-anchor="end" font-size="9">${escapeHtml(axisLabel(v))}</text>`,
      );
    }

    // x axis ticks: first, middle decade, last.
    const midTick = Math.round((minYear + maxYear) / 2 / 10) * 10;
    const ticks = [...new Set([minYear, midTick, maxYear])].filter(
      (t) => t >= minYear && t <= maxYear,
    );
    for (const t of ticks) {
      const anchor = t === minYear ? 'start' : t === maxYear ? 'end' : 'middle';
      parts.push(
        `<line x1="${xOf(t).toFixed(1)}" y1="${BASE}" x2="${xOf(t).toFixed(1)}" y2="${BASE + 3}" stroke="var(--border-default)" stroke-width="0.8"></line>`,
        `<text x="${xOf(t).toFixed(1)}" y="${BASE + 13}" text-anchor="${anchor}" font-size="9">${t}</text>`,
      );
    }
    parts.push(
      `<text x="${(PAD_L + PW / 2).toFixed(1)}" y="${H - 1}" text-anchor="middle" font-size="9" fill="var(--text-tertiary)">Year named in the list</text>`,
    );

    // Segments: a missing year BREAKS the line. Nothing is zero-filled.
    const segments: number[][] = [];
    let cur: number[] = [];
    for (const y of o.years) {
      if (cur.length && y - cur[cur.length - 1] > 1) {
        segments.push(cur);
        cur = [];
      }
      cur.push(y);
    }
    if (cur.length) segments.push(cur);

    for (const seg of segments) {
      const pts = seg.map((y) => `${xOf(y).toFixed(1)},${yOf(o.byYear.get(y) ?? 0, top).toFixed(1)}`);
      if (seg.length > 1) {
        parts.push(
          `<path d="M${xOf(seg[0]).toFixed(1)},${BASE} L${pts.join(' L')} L${xOf(seg[seg.length - 1]).toFixed(1)},${BASE} Z" fill="${o.colour}" opacity="0.16"></path>`,
          `<polyline points="${pts.join(' ')}" fill="none" stroke="${o.colour}" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"></polyline>`,
        );
      }
    }
    for (const y of o.years) {
      const n = o.byYear.get(y) ?? 0;
      parts.push(
        `<circle cx="${xOf(y).toFixed(1)}" cy="${yOf(n, top).toFixed(1)}" r="${o.years.length > 12 ? 1.5 : 2.6}" fill="${o.colour}"></circle>`,
      );
    }
    if (!o.years.length) {
      parts.push(
        `<text x="${(PAD_L + PW / 2).toFixed(1)}" y="${(PAD_T + PH / 2).toFixed(1)}" text-anchor="middle" font-size="9" fill="var(--text-tertiary)">No dated records</text>`,
      );
    }

    // Hover bands cover EVERY year, so an empty year says so rather than
    // sitting there as dead space.
    for (let y = minYear; y <= maxYear; y++) {
      const n = o.byYear.get(y);
      const bx = Math.max(PAD_L, xOf(y) - step / 2);
      const bw = Math.min(PAD_L + PW - bx, step);
      if (!(bw > 0)) continue;
      const suffix = y > datedThrough && hasBand ? '\nList names from this year carry no year' : '';
      const tip =
        n == null
          ? `${o.label}\n${y}\nNo records under this list name${suffix}`
          : `${o.label}\n${y}\n${num(n)} record${n === 1 ? '' : 's'}${suffix}`;
      parts.push(
        `<rect x="${bx.toFixed(1)}" y="${PAD_T}" width="${bw.toFixed(2)}" height="${PH}" fill="transparent"
           data-tip="${tipAttr(tip)}" aria-label="${tipAttr(tip.replace(/\n/g, ', '))}"></rect>`,
      );
    }

    const name = `${o.label}, records per year from ${minYear} to ${maxYear}. Peak ${o.peakYear ?? '—'}, ${num(o.peakN)}.`;
    return `<svg class="chart" viewBox="0 0 ${W} ${H}" role="group" aria-label="${escapeHtml(name)}">${parts.join('')}</svg>`;
  }

  // ---- one card -----------------------------------------------------------

  function card(o: Occ, i: number): string {
    const top = sharedScale ? sharedMax : Math.max(1, o.peakN);
    const datedShare = o.total > 0 ? o.dated / o.total : 0;
    const thin = datedShare < 0.5;
    const covTip = `${o.label}\n${num(o.dated)} records dated by list name (${pct(datedShare)})\n${num(o.undated)} with no year in the list name`;
    return `<div class="lists-card" data-occ="${i}" role="button" tabindex="0" aria-pressed="false"
        aria-label="${escapeHtml(`Select ${o.label}, ${num(o.total)} records`)}">
      <div class="lists-card-head">
        <span class="lists-title">${escapeHtml(o.label)}</span>
        <span class="lists-total num" data-tip="${tipAttr(`${o.label}\n${num(o.total)} records in total\n${pct(listed > 0 ? o.total / listed : 0)} of all records in a named list`)}">${num(o.total)}</span>
      </div>
      <div class="lists-when">${escapeHtml(o.cadence)}</div>
      <div class="lists-cov" role="group" aria-label="${escapeHtml(`${o.label}: ${num(o.dated)} records dated by list name, ${num(o.undated)} undated`)}">
        <div role="img" style="width:${(datedShare * 100).toFixed(1)}%;background:${o.colour}"
          aria-label="${escapeHtml(`${num(o.dated)} dated, ${pct(datedShare)}`)}" data-tip="${tipAttr(covTip)}"></div>
        <div role="img" style="width:${((1 - datedShare) * 100).toFixed(1)}%;background:#cfc9ba"
          aria-label="${escapeHtml(`${num(o.undated)} undated, ${pct(1 - datedShare)}`)}" data-tip="${tipAttr(covTip)}"></div>
      </div>
      <div class="lists-cov-label">${num(o.dated)} dated · ${num(o.undated)} undated${
        thin
          ? ` <span class="lists-warn">— the chart below covers only the dated ${pct(datedShare, 0)}</span>`
          : ''
      }</div>
      <div class="chart-wrap">${miniChart(o, top)}</div>
      <div class="lists-foot">${
        o.peakYear == null
          ? 'No dated list years.'
          : `Biggest dated list: ${o.peakYear}, ${num(o.peakN)} records.`
      } <span class="lists-hint">${escapeHtml(o.blurb)}</span></div>
    </div>`;
  }

  // ---- page ---------------------------------------------------------------

  const rawStrings = core.dicts.list.length;

  const twoListShare = listed > 0 ? twoLists / listed : 0;
  const adAvg = averageOver(ad, datedThrough - 9, datedThrough);
  const mbAvg = averageOver(mb, datedThrough - 9, datedThrough);

  function averageOver(o: Occ | undefined, from: number, to: number): number {
    if (!o) return NaN;
    let s = 0;
    let k = 0;
    for (let y = from; y <= to; y++) {
      const n = o.byYear.get(y);
      if (n != null) {
        s += n;
        k++;
      }
    }
    return k > 0 ? s / k : NaN;
  }

  const rows: { label: string; a: string; b: string; tip: string }[] = [
    {
      label: 'Records in the list',
      a: num(ad?.total ?? 0),
      b: num(mb?.total ?? 0),
      tip: 'Every record whose list name normalises to this occasion, dated or not.',
    },
    {
      label: 'Share of all listed records',
      a: pct(listed > 0 ? (ad?.total ?? 0) / listed : 0),
      b: pct(listed > 0 ? (mb?.total ?? 0) / listed : 0),
      tip: `Out of ${num(listed)} records that belong to any named list.`,
    },
    {
      label: 'Dated list years',
      a: `${ad?.years.length ?? 0} (${ad?.years[0] ?? '—'}–${ad?.years[(ad?.years.length ?? 1) - 1] ?? '—'})`,
      b: `${mb?.years.length ?? 0} (${mb?.years[0] ?? '—'}–${mb?.years[(mb?.years.length ?? 1) - 1] ?? '—'})`,
      tip: 'Distinct years written into the list name.',
    },
    {
      label: 'Biggest single list',
      a: `${ad?.peakYear ?? '—'} · ${num(ad?.peakN ?? 0)}`,
      b: `${mb?.peakYear ?? '—'} · ${num(mb?.peakN ?? 0)}`,
      tip: 'The largest number of records ever carried by one edition of this list.',
    },
    {
      label: `Average per list, ${datedThrough - 9}–${datedThrough}`,
      a: Number.isFinite(adAvg) ? num(Math.round(adAvg)) : '—',
      b: Number.isFinite(mbAvg) ? num(Math.round(mbAvg)) : '—',
      tip: 'Mean of the last ten reliably dated editions.',
    },
    {
      label: 'Records with no year in the name',
      a: num(ad?.undated ?? 0),
      b: num(mb?.undated ?? 0),
      tip: 'Gazetted under a bare list name such as “Australia Day”, with no year attached.',
    },
  ];

  root.innerHTML = `
  <style>
    .lists-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(290px,100%),1fr));gap:var(--space-md);}
    .lists-card{border:1px solid var(--border-subtle);border-radius:var(--radius-md);background:var(--bg-surface);
      padding:var(--space-md);min-width:0;cursor:pointer;transition:opacity var(--transition-normal),border-color var(--transition-fast),background var(--transition-fast);}
    .lists-card.is-dim{opacity:.32;}
    .lists-card.is-sel{border-color:var(--border-strong);background:var(--bg-elevated);box-shadow:0 2px 10px rgba(20,24,33,.08);}
    .lists-card-head{display:flex;align-items:baseline;gap:var(--space-sm);}
    .lists-title{min-width:0;flex:1 1 auto;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-weight:600;}
    .lists-total{flex-shrink:0;font-size:var(--font-size-lg);font-weight:600;color:var(--accent-secondary);}
    .lists-when{font-size:var(--font-size-xs);color:var(--text-tertiary);}
    .lists-cov{display:flex;height:8px;border-radius:4px;overflow:hidden;background:var(--bg-elevated);margin:var(--space-sm) 0 2px;}
    .lists-cov>div{display:block;height:100%;flex:none;min-width:0;}
    .lists-cov-label{font-size:var(--font-size-xs);color:var(--text-tertiary);margin-bottom:var(--space-xs);}
    .lists-warn{color:var(--status-warn);}
    .lists-foot{font-size:var(--font-size-xs);color:var(--text-secondary);margin-top:var(--space-xs);}
    .lists-hint{color:var(--text-tertiary);}
    .lists-legend-btn{font:inherit;font-size:var(--font-size-sm);background:none;border:0;padding:.25rem .35rem;
      min-height:40px;cursor:pointer;border-radius:var(--radius-sm);color:var(--text-secondary);}
    .lists-legend-btn:hover{background:var(--bg-hover);color:var(--text-primary);}
    .lists-legend-btn.is-dim{opacity:.4;}
    .lists-legend-btn.is-sel{background:var(--accent-wash);color:var(--text-primary);font-weight:600;}
    .lists-two{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(260px,100%),1fr));gap:var(--space-md);}
    .lists-mini{border:1px solid var(--border-subtle);border-radius:var(--radius-md);padding:var(--space-md);min-width:0;}
    .lists-mini h3{color:var(--accent-secondary);}
    .lists-clear{min-height:44px;}
    .lists-band-key{width:14px;height:14px;border-radius:3px;flex-shrink:0;background:#b3701a;opacity:.35;border:1px dashed #b3701a;}
  </style>

  <div class="view-head">
    <h1>The lists</h1>
    <p class="lede">Most Order of Australia honours arrive in exactly two batches a year: the Australia Day
      list on 26 January, and the June list — the Queen’s Birthday list until the accession, the King’s
      Birthday list since. Bravery and military awards keep no such calendar; they are
      ${gloss('gazette', 'gazetted')} continuously, as incidents and nominations are assessed. Four
      different cadences, which only separate when you draw them side by side.</p>
  </div>

  <div class="stats">
    <div class="stat">
      <div class="stat-value" data-tip="${tipAttr(`${num(twoLists)} records in the Australia Day and Monarch's Birthday lists\n${pct(twoListShare)} of the ${num(listed)} records that belong to any named list`)}">${num(twoLists)}</div>
      <div class="stat-label">In the two twice-yearly lists — ${pct(twoListShare)} of every record that belongs to a named list</div>
    </div>
    <div class="stat">
      <div class="stat-value" data-tip="${tipAttr(`${num(listed)} of ${num(records)} records carry a named list\n${pct(listed / records)} of the register`)}">${num(listed)}</div>
      <div class="stat-label">Records in a named ${gloss('honours-list', 'honours list')} at all — ${pct(listed / records)} of the register</div>
    </div>
    <div class="stat">
      <div class="stat-value" data-tip="${tipAttr(`${num(noList)} records belong to no named list\n${pct(noList / records)} of the register\nLong-service and campaign medals are gazetted individually`)}">${num(noList)}</div>
      <div class="stat-label">Records in <strong>no named list</strong> — long-service and campaign medals, gazetted one at a time</div>
    </div>
    <div class="stat">
      <div class="stat-value" data-tip="${tipAttr(`${num(core.covid)} records flagged as part of the COVID-19 honours roll`)}">${num(core.covid)}</div>
      <div class="stat-label">On the COVID-19 honour roll</div>
    </div>
    <div class="stat">
      <div class="stat-value" data-tip="${tipAttr(`${num(core.honorary)} honorary awards to people who are not Australian citizens`)}">${num(core.honorary)}</div>
      <div class="stat-label">${gloss('honorary-award', 'Honorary awards')} to non-citizens</div>
    </div>
  </div>

  <section class="panel">
    <div class="panel-head">
      <h2>Four cadences, one shared scale</h2>
      <p class="panel-sub">One small chart per occasion. Each line is the number of records whose list name
        gives that year. On the shared scale every chart uses the same vertical axis — 0 to
        ${num(sharedMax)} records — so the two calendar lists visibly dwarf everything else; switch to
        <em>own scale</em> to read the shape of the small ones. Click any chart to select that occasion and
        dim the rest.</p>
    </div>

    <div class="controls">
      <div class="control-group" role="group" aria-label="Vertical scale">
        <button type="button" data-scale="shared">Shared scale</button>
        <button type="button" data-scale="own">Own scale</button>
      </div>
      <button type="button" class="pill lists-clear" data-clear hidden>Show all occasions ✕</button>
    </div>

    <div class="lists-grid" id="lists-grid"></div>

    <div class="legend" id="lists-legend"></div>
    <div class="legend" aria-label="How to read these charts">
      <span class="legend-item"><span class="lists-band-key"></span>Shaded: list names after ${datedThrough} carry no year</span>
      <span class="legend-item"><span class="swatch" style="background:var(--bg-base);border-color:var(--border-default)"></span>A gap in a line means no list under that name that year — nothing is filled in with zero</span>
    </div>

    ${
      hasBand
        ? `<p class="note"><strong>Read the shaded strip carefully.</strong> The lists did not stop after
      ${datedThrough}. Honours were still gazetted — but from ${datedThrough + 1} the register records
      the list simply as “Australia Day” or “The King’s Birthday”, with no year in the name, so those
      records sit in the undated totals on each card instead of on the line. The dip at the right-hand
      edge is a naming change, not a fall in awards.</p>`
        : ''
    }
  </section>

  <section class="panel">
    <div class="panel-head">
      <h2>The two general lists, side by side</h2>
      <p class="panel-sub">Australia Day and the Monarch’s Birthday are the twin engines of the
        ${gloss('order', 'Order of Australia')}. They are close to the same size, and have been for fifty
        years. Hover any figure for what it counts.</p>
    </div>
    <div class="scroller">
      <table>
        <caption class="panel-sub" style="text-align:left;padding-bottom:.5rem">Records by named list. “Undated” means the list name carries no year, not that the award is undated.</caption>
        <thead>
          <tr><th scope="col">Measure</th>
            <th scope="col" class="n">Australia Day</th>
            <th scope="col" class="n">Monarch’s Birthday</th></tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (r) => `<tr>
            <th scope="row" style="background:transparent;white-space:normal">${escapeHtml(r.label)}</th>
            <td class="n" data-tip="${tipAttr(`Australia Day — ${r.label}\n${r.a}\n${r.tip}`)}">${escapeHtml(r.a)}</td>
            <td class="n" data-tip="${tipAttr(`Monarch's Birthday — ${r.label}\n${r.b}\n${r.tip}`)}">${escapeHtml(r.b)}</td>
          </tr>`,
            )
            .join('')}
        </tbody>
      </table>
    </div>
    <p class="note">The register publishes the list a record belongs to, and the award a record received,
      but this site's aggregate does not cross the two — so the split of each list by
      ${gloss('post-nominal', 'level')} (${gloss('ac', 'AC')}, ${gloss('ao', 'AO')},
      ${gloss('am', 'AM')}, ${gloss('oam', 'OAM')}) is not derivable here and is not shown. Search a
      recipient to see both fields together.</p>
  </section>

  <section class="panel">
    <div class="panel-head">
      <h2>Two cohorts that stand outside the calendar</h2>
      <p class="panel-sub">Small groups, worth naming because they behave differently from everything above.</p>
    </div>
    <div class="lists-two">
      <div class="lists-mini">
        <h3>${num(core.covid)} on the COVID-19 honour roll</h3>
        <p class="panel-sub">Records flagged in the register as part of the COVID-19 honour roll —
          recognition of pandemic service, folded into the ordinary lists rather than gazetted as a
          system of its own. It is a rounding error against the ${num(records)} records here, and a
          reminder that the honours system responds to events slowly.</p>
      </div>
      <div class="lists-mini">
        <h3>${num(core.honorary)} honorary awards</h3>
        <p class="panel-sub">An ${gloss('honorary-award')} goes to someone who is not an Australian
          citizen; recipients do not use the post-nominal letters. These arrive on their own schedule —
          the raw list strings include dozens of one-off “Honorary Appointment — General Division”
          entries with a month attached rather than a list.</p>
      </div>
    </div>
  </section>

  <section class="panel">
    <div class="panel-head">
      <h2>Why these counts differ from a naive grouping</h2>
    </div>
    <p class="panel-sub">The register stores the list as free text, typed rather than chosen. “Australia
      Day”, “Australia Day 2019” and “Australia Day 2020 Honours List” are the same list under three
      different strings; the Queen’s Birthday became the King’s Birthday partway through; and there is at
      least one misspelling in the file. ${num(rawStrings)} distinct raw strings appear in the data, and
      they are normalised here to the six occasions above. Group by the raw text instead and you get
      ${num(rawStrings)} tiny lists, most of them the same handful of events.</p>
    <p class="panel-sub" style="margin-top:var(--space-md)">And the biggest number on this page is the one
      that is <em>not</em> a list: ${num(noList)} records — ${pct(noList / records)} of the register —
      belong to no named list at all. Long-service and campaign medals are gazetted individually as
      people qualify. That is how the system works, not a hole in the data.</p>
    <p style="margin-top:var(--space-md)"><button type="button" class="pill" data-go-register>See what the whole register is made of →</button></p>
  </section>`;

  // ---- wiring -------------------------------------------------------------

  const grid = root.querySelector<HTMLElement>('#lists-grid')!;
  const legend = root.querySelector<HTMLElement>('#lists-legend')!;
  const clearBtn = root.querySelector<HTMLButtonElement>('[data-clear]')!;

  legend.innerHTML = occs
    .map(
      (o, i) => `<button type="button" class="legend-item lists-legend-btn" data-occ="${i}"
        aria-pressed="false"
        data-tip="${tipAttr(`${o.label}\n${num(o.total)} records\n${o.cadence}`)}">
        <span class="swatch" style="background:${o.colour}"></span>${escapeHtml(o.label)}</button>`,
    )
    .join('');

  function drawGrid(): void {
    grid.innerHTML = occs.map((o, i) => card(o, i)).join('');
    applySelection();
  }

  function applySelection(): void {
    for (const el of root.querySelectorAll<HTMLElement>('[data-occ]')) {
      const i = Number(el.dataset.occ);
      const sel = selected >= 0 && i === selected;
      el.classList.toggle('is-sel', sel);
      el.classList.toggle('is-dim', selected >= 0 && !sel);
      el.setAttribute('aria-pressed', sel ? 'true' : 'false');
    }
    clearBtn.hidden = selected < 0;
  }

  function setScale(shared: boolean): void {
    sharedScale = shared;
    for (const b of root.querySelectorAll<HTMLButtonElement>('[data-scale]')) {
      b.setAttribute('aria-pressed', (b.dataset.scale === 'shared') === shared ? 'true' : 'false');
    }
    drawGrid();
  }

  function toggle(i: number): void {
    selected = selected === i ? -1 : i;
    applySelection();
  }

  root.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('.glossary-link')) return;

    const scaleBtn = target.closest<HTMLElement>('[data-scale]');
    if (scaleBtn) {
      setScale(scaleBtn.dataset.scale === 'shared');
      return;
    }
    if (target.closest('[data-clear]')) {
      selected = -1;
      applySelection();
      return;
    }
    if (target.closest('[data-go-register]')) {
      ctx.go('register');
      return;
    }
    const occEl = target.closest<HTMLElement>('[data-occ]');
    if (occEl) toggle(Number(occEl.dataset.occ));
  });

  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const occEl = (e.target as HTMLElement).closest<HTMLElement>('.lists-card[data-occ]');
    if (!occEl) return;
    e.preventDefault();
    toggle(Number(occEl.dataset.occ));
  });

  setScale(sharedScale);
};
